/**
 * Zod schemas mirroring server/api_v2.py.
 *
 * The Python contract tests in tests/server/test_api_v2.py guarantee the
 * server emits these shapes; this file mirrors them on the client so any
 * drift surfaces as a runtime parse error during development. Keep names
 * and field order aligned with api_v2.py for easier review.
 */
import { z } from 'zod'

// ── Shared primitives ────────────────────────────────────────────

export const Iso = z.string()

export const AlertLevel = z.enum(['warning', 'critical'])
export type AlertLevel = z.infer<typeof AlertLevel>

export const EmbeddedAlert = z.object({
  level: AlertLevel,
  category: z.string(),
  message: z.string(),
})
export type EmbeddedAlert = z.infer<typeof EmbeddedAlert>

// ── /api/sites ───────────────────────────────────────────────────

export const PortfolioCard = z.object({
  id: z.string(),
  name: z.string(),
  address: z.string().nullable(),
  status: z.string(),
  timezone: z.string(),
  last_heartbeat: Iso.nullable(),
  cameras_total: z.number().int().nonnegative(),
  cameras_online: z.number().int().nonnegative(),
  edges_online: z.number().int().nonnegative(),
  edges_total: z.number().int().nonnegative(),
  events_today: z.number().int().nonnegative(),
  alerts_pending: z.number().int().nonnegative(),
  last_analysis_at: Iso.nullable(),
  first_camera_id: z.string().nullable(),
  zone_label: z.string(),
  /** 5 Oct 2026: active cameras with no photo for 2 h, and the oldest last photo among them. */
  cameras_offline: z.number().int().nonnegative().optional(),
  camera_offline_since: Iso.nullable().optional(),
})
export type PortfolioCard = z.infer<typeof PortfolioCard>

export const SitesListResponse = z.object({
  sites: z.array(PortfolioCard),
})
export type SitesListResponse = z.infer<typeof SitesListResponse>

// ── /api/sites/{id} ──────────────────────────────────────────────

export const Site = z.object({
  id: z.string(),
  name: z.string(),
  address: z.string().nullable(),
  start_date: z.string().nullable(),
  last_heartbeat: Iso.nullable(),
  status: z.string(),
  timezone: z.string(),
  created_at: Iso,
  /** 5 Oct 2026: photo checks per hour (2 for Costa 3, else 1). */
  checks_per_hour: z.number().int().optional(),
})
export type Site = z.infer<typeof Site>

export const SiteStats = z.object({
  cameras_total: z.number().int().nonnegative(),
  cameras_online: z.number().int().nonnegative(),
  edges_total: z.number().int().nonnegative(),
  edges_online: z.number().int().nonnegative(),
  events_today: z.number().int().nonnegative(),
  alerts_pending: z.number().int().nonnegative(),
  last_analysis_at: Iso.nullable(),
})
export type SiteStats = z.infer<typeof SiteStats>

export const EdgeDevice = z.object({
  edge_id: z.string(),
  tailscale_ip: z.string().nullable(),
  status: z.string(),
  last_heartbeat: Iso.nullable(),
  cameras_count: z.number().int().nonnegative(),
})
export type EdgeDevice = z.infer<typeof EdgeDevice>

export const CameraSummary = z.object({
  camera_id: z.string(),
  zone: z.string(),
  edge_id: z.string().nullable(),
  events_today: z.number().int().nonnegative(),
  status: z.string(),
  display_name: z.string(),
  // WP2A3B: last time this camera produced a usable (non-null image_path)
  // event -- used by the compact camera card instead of an hourly analysis
  // list or a raw capture count.
  last_usable_image_at: Iso.nullable(),
  // 5 Oct 2026: latest photo Zonoo analysed in the day's window -- the
  // photo the Site update text is based on. Optional for older payloads.
  last_reviewed_at: Iso.nullable().optional(),
  view_changed_since: Iso.nullable().optional(),
})
export type CameraSummary = z.infer<typeof CameraSummary>

export const PendingAlert = z.object({
  id: z.number().int(),
  level: AlertLevel,
  category: z.string(),
  message: z.string(),
  created_at: Iso,
  camera_id: z.string().nullable(),
  // WP2A3A: the pending alert's camera, already resolved to an owner-
  // facing name and a formatted zone label -- null only when the alert
  // has no linked camera at all (never a raw camera_id or "No zone").
  camera_display_name: z.string().nullable(),
  zone: z.string().nullable(),
  // WP2A3B: null for legacy rows predating this classification, or for any
  // category other than 'progress'. Used client-side to decide whether a
  // progress-category alert belongs in "Needs attention" (stall /
  // clarification_needed) or is a routine observation to leave out
  // (positive / material_change / null).
  progress_kind: z.string().nullable(),
})
export type PendingAlert = z.infer<typeof PendingAlert>

export const AlertHistoryRow = z.object({
  id: z.number().int(),
  event_id: z.number().int().nullable(),
  level: AlertLevel,
  category: z.string(),
  message: z.string(),
  acknowledged: z.boolean(),
  created_at: Iso,
  camera_id: z.string().nullable(),
  camera_display_name: z.string().nullable(),
  zone: z.string().nullable(),
})
export type AlertHistoryRow = z.infer<typeof AlertHistoryRow>

export const AlertsListResponse = z.object({
  alerts: z.array(AlertHistoryRow),
})
export type AlertsListResponse = z.infer<typeof AlertsListResponse>

export const Phase = z.object({
  id: z.number().int(),
  name: z.string(),
  order: z.number().int(),
  planned_start: z.string(),
  planned_end: z.string(),
  expected_activity: z.string().nullable(),
  expected_machinery: z.string().nullable(),
  actual_start: z.string().nullable(),
  actual_end: z.string().nullable(),
  close_votes: z.number().int().nonnegative(),
  close_last_vote_date: z.string().nullable(),
})
export type Phase = z.infer<typeof Phase>

export const TimelineBar = Phase.extend({
  left_pct: z.number(),
  width_pct: z.number(),
  actual_left_pct: z.number(),
  actual_width_pct: z.number(),
  start_fmt: z.string(),
  end_fmt: z.string(),
  actual_start_fmt: z.string().nullable(),
  actual_end_fmt: z.string().nullable(),
})
export type TimelineBar = z.infer<typeof TimelineBar>

export const TimelineMonth = z.object({
  label: z.string(),
  pct: z.number(),
  /** 4 Oct 2026: true for a month start (drawn stronger). Optional so
   *  an older server payload still parses. */
  major: z.boolean().optional(),
})
export type TimelineMonth = z.infer<typeof TimelineMonth>

export const Timeline = z.object({
  bars: z.array(TimelineBar),
  months: z.array(TimelineMonth),
  today_pct: z.number().nullable(),
})
export type Timeline = z.infer<typeof Timeline>

export const Document = z.object({
  id: z.number().int(),
  filename: z.string(),
  doc_type: z.string(),
  uploaded_at: Iso,
})
export type Document = z.infer<typeof Document>

// WP-SCHED: an uploaded/extracted schedule is a DRAFT until someone
// reviews and accepts it (server/project_context.py). `status` is
// 'draft' | 'accepted' | 'superseded' | 'discarded'.
export const SchedulePhase = z.object({
  name: z.string(),
  order: z.number().int(),
  planned_start: z.string(),
  planned_end: z.string(),
  expected_activity: z.string(),
  expected_machinery: z.string(),
})
export type SchedulePhase = z.infer<typeof SchedulePhase>

export const ScheduleVersion = z.object({
  id: z.number().int(),
  site_id: z.string(),
  status: z.enum(['draft', 'accepted', 'superseded', 'discarded']),
  project_brief: z.string(),
  phases: z.array(SchedulePhase),
  source_document_ids: z.array(z.number().int()),
  created_at: Iso,
  created_by_user_id: z.string().nullable(),
  accepted_at: Iso.nullable(),
  accepted_by_user_id: z.string().nullable(),
})
export type ScheduleVersion = z.infer<typeof ScheduleVersion>

export const ScheduleVersionsResponse = z.object({
  versions: z.array(ScheduleVersion),
  draft_id: z.number().int().nullable(),
  accepted_id: z.number().int().nullable(),
})
export type ScheduleVersionsResponse = z.infer<typeof ScheduleVersionsResponse>

export const ScheduleVersionResponse = z.object({
  version: ScheduleVersion,
})
export type ScheduleVersionResponse = z.infer<typeof ScheduleVersionResponse>

// WP2A1.5: the trusted Weekly Owner Summary -- like DailyBrief, every
// string here is already fully composed and trust-checked server-side
// (server/weekly_brief.py); the frontend only renders them. `period_start`
// / `period_end` are nullable because the safe fallback path omits them
// when generation fails validation.
export const WeeklySummary = z.object({
  title: z.string(),
  period_start: Iso.nullable(),
  period_end: Iso.nullable(),
  monitoring_line: z.string(),
  paragraph: z.string(),
  suggested_action: z.string().nullable(),
  generated_at: Iso,
})
export type WeeklySummary = z.infer<typeof WeeklySummary>

// Zonoo Site Update: a short, natural-language description composed
// from already-stored camera analyses (server/site_update.py) -- never
// the bare "no accepted status" fallback the old cards used. `paragraph`
// is always a real, presentation-safe description or an honest empty
// state; `contractor_note` is a separate, independently-attributed
// sentence from an accepted Project Update (never blended into
// `paragraph`) and is null when there is no accepted update.
// `observed_at` is null only when there is nothing to describe yet.
export const SiteUpdate = z.object({
  paragraph: z.string(),
  observed_at: Iso.nullable(),
  contractor_note: z.string().nullable(),
  // WP-SCHED: a short, separately-labeled planned/observed/reported
  // comparison -- present only when the site has an accepted schedule
  // with something relevant to say for this period (server/site_update.py).
  // Never blended into `paragraph`; null just means there is nothing to
  // add, not an error.
  planning_note: z.string().nullable(),
  period_start: z.string(),
  period_end: z.string(),
})
export type SiteUpdate = z.infer<typeof SiteUpdate>

// WP2A3B + Zonoo Site Update/Needs Attention split: an owner-facing
// action item requiring review or follow-up -- an active accepted
// project constraint still missing a confirmed delivery/restart date,
// an overdue phase close-confirmation, or a prolonged camera monitoring
// gap (server/needs_attention.py). The site payload now carries a LIST
// of these (0 or more), not a single nullable one.
export const NeedsAttentionConstraint = z.object({
  id: z.string(),
  title: z.string(),
  body: z.string(),
  suggested_action: z.string(),
  /** 9 Oct 2026: sent back when the item is dismissed (which occurrence). */
  dismiss_key: z.string().optional(),
})
export type NeedsAttentionConstraint = z.infer<typeof NeedsAttentionConstraint>

export const SitePayload = z.object({
  site: Site,
  stats: SiteStats,
  zone_label: z.string(),
  cameras: z.array(CameraSummary),
  alerts_pending: z.array(PendingAlert),
  project_brief: z.string().nullable(),
  phases: z.array(Phase),
  current_phases: z.array(z.string()),
  timeline: Timeline,
  documents: z.array(Document),
  weekly_summary: WeeklySummary.nullable(),
  close_votes_manual_threshold: z.number().int().positive(),
  needs_attention_constraints: z.array(NeedsAttentionConstraint),
  site_update_week: SiteUpdate.nullable(),
})
export type SitePayload = z.infer<typeof SitePayload>

// ── /api/sites/{id}/analyses ─────────────────────────────────────

export const BatchAnalysisEntry = z.object({
  id: z.number().int(),
  type: z.literal('batch'),
  period_start: Iso,
  period_end: Iso,
  summary: z.string(),
  relevance: z.string().nullable(),
  created_at: Iso,
})
export type BatchAnalysisEntry = z.infer<typeof BatchAnalysisEntry>

/** Period-summary schema fields surfaced by the v2 reporting engine (specs/18).
 *  All optional so legacy rows (which only have the `summary` string) still parse. */
export const SummaryEntry = z.object({
  id: z.number().int(),
  type: z.literal('summary'),
  level: z.enum(['hourly', 'daily', 'weekly']),
  period_start: Iso,
  period_end: Iso,
  summary: z.string(),
  highlights: z.array(z.string()),
  created_at: Iso,
  // v2 structured fields — optional for backward compat with any row still
  // carrying the legacy free-text `summary` only.
  no_change: z.boolean().optional(),
  relevance: z.enum(['low', 'medium', 'high']).nullable().optional(),
  reason: z.string().nullable().optional(),
})
export type SummaryEntry = z.infer<typeof SummaryEntry>

export const AnalysisEntry = z.discriminatedUnion('type', [BatchAnalysisEntry, SummaryEntry])
export type AnalysisEntry = z.infer<typeof AnalysisEntry>

// WP2A1: the trusted Daily Owner Brief. Deliberately just five plain
// strings/nullables -- every fact and every trust rule is already applied
// server-side (server/daily_brief.py); the frontend only renders them.
export const DailyBrief = z.object({
  title: z.string(),
  monitoring_line: z.string(),
  paragraph: z.string(),
  suggested_action: z.string().nullable(),
  generated_at: Iso,
})
export type DailyBrief = z.infer<typeof DailyBrief>

// WP2A2: structured evidence behind a Daily Brief / Weekly Summary card.
// Every field here is presentation-safe -- no internal database ids, file
// paths, prompts, or other technical metadata (server/evidence.py).
export const EvidenceImage = z.object({
  type: z.literal('image'),
  camera_display_name: z.string(),
  zone: z.string().nullable(),
  captured_at: Iso,
  image_url: z.string(),
  description: z.string(),
  relation: z.string(),
})
export type EvidenceImage = z.infer<typeof EvidenceImage>

export const EvidenceStatus = z.enum(['complete', 'incomplete', 'unavailable', 'not_required'])
export type EvidenceStatus = z.infer<typeof EvidenceStatus>

// Zonoo: AI Photo Comparison -- lifecycle of the AI visual comparison
// for the currently-selected image pair. 'not_applicable' covers every
// case where there isn't a genuine, viewpoint-compatible pair to send
// (limitation_note explains why); 'pending' while the background AI
// call is in flight; 'ready' once visual_finding holds its result;
// 'failed' when the call errored or hit the monthly budget cap.
export const ComparisonStatus = z.enum(['not_applicable', 'pending', 'ready', 'failed'])
export type ComparisonStatus = z.infer<typeof ComparisonStatus>

export const BriefEvidence = z.object({
  site_id: z.string(),
  brief_type: z.enum(['daily', 'weekly']),
  period_start: z.string(),
  period_end: z.string(),
  // Coverage classification -- kept for other/back-compat consumers, but
  // the WP2A3 Visual Evidence Experience drawer no longer shows either
  // field (that belongs in the brief/camera status, not repeated here).
  status: EvidenceStatus,
  status_explanation: z.string(),
  is_valid_comparison: z.boolean(),
  // An honest note about why no valid comparison could be made (missing
  // baseline, no clear daylight image, a changed camera viewpoint, ...)
  // -- null when a valid comparison exists.
  limitation_note: z.string().nullable(),
  comparison_status: ComparisonStatus,
  // The AI-generated finding (server.evidence_comparison) -- one or two
  // short sentences describing what the two selected images actually
  // show. Only ever populated once comparison_status === 'ready'; null
  // otherwise (the drawer shows a loading/failure state instead).
  visual_finding: z.string().nullable(),
  images: z.array(EvidenceImage),
  // WP2A3 item 4: the subject line names the actual construction work
  // (never a raw camera id, never "Category · Zone") -- always present.
  // source_date_line distinguishes who supplied an update from when it
  // was recorded/accepted in Zonoo -- null when there's no specific
  // update to cite (a plain reporting-period progress comparison).
  subject_line: z.string(),
  source_date_line: z.string().nullable(),
  brief_paragraph: z.string(),
})
export type BriefEvidence = z.infer<typeof BriefEvidence>

export const BriefFeedbackRating = z.enum(['correct', 'incorrect', 'not_enough_evidence'])
export type BriefFeedbackRating = z.infer<typeof BriefFeedbackRating>

export const BriefFeedback = z.object({
  rating: BriefFeedbackRating.nullable(),
  updated_at: Iso.nullable(),
})
export type BriefFeedback = z.infer<typeof BriefFeedback>

export const BriefFeedbackSaved = z.object({
  status: z.literal('ok'),
  rating: BriefFeedbackRating,
  updated_at: Iso,
})
export type BriefFeedbackSaved = z.infer<typeof BriefFeedbackSaved>

export const SiteAnalysesResponse = z.object({
  entries: z.array(AnalysisEntry),
  daily_summary: SummaryEntry.nullable(),
  daily_brief: DailyBrief.nullable(),
  site_update_today: SiteUpdate.nullable(),
})
export type SiteAnalysesResponse = z.infer<typeof SiteAnalysesResponse>

export const CameraAnalysesResponse = z.object({
  entries: z.array(BatchAnalysisEntry),
})
export type CameraAnalysesResponse = z.infer<typeof CameraAnalysesResponse>

// ── /api/chat/views (CDV-029 stash) ─────────────────────────────

export const StashedViewCreated = z.object({
  id: z.string().min(1),
  expires_at: Iso,
})
export type StashedViewCreated = z.infer<typeof StashedViewCreated>

export const StashedViewPayload = z.object({
  id: z.string().min(1),
  payload: z.string().min(1),
  expires_at: Iso,
})
export type StashedViewPayload = z.infer<typeof StashedViewPayload>

// ── /api/sites/{id}/captures (CDV-005) ──────────────────────────

export const Capture = z.object({
  event_id: z.number().int(),
  camera_id: z.string(),
  captured_at: Iso,
  image_url: z.string(),
})
export type Capture = z.infer<typeof Capture>

export const SiteCapturesResponse = z.object({
  site_id: z.string(),
  date: z.string(),
  captures: z.array(Capture),
})
export type SiteCapturesResponse = z.infer<typeof SiteCapturesResponse>

// ── /api/sites/{id}/events ───────────────────────────────────────

export const Event = z.object({
  id: z.number().int(),
  camera_id: z.string(),
  timestamp: Iso,
  trigger: z.string(),
  status: z.string(),
  description: z.string(),
  relevance: z.string(),
  alert: EmbeddedAlert.nullable(),
  has_image: z.boolean(),
  // Camera Page UI/UX item 3: the honest analysis state for this exact
  // capture, derived server-side from its actual recorded status --
  // 'analyzed' | 'awaiting' | 'outside_hours' | 'not_selected' | 'failed'
  // | 'unknown'. Optional only for schema resilience; the server always
  // sends it.
  analysis_state: z.string().optional(),
  analysis_state_label: z.string().optional(),
  // Camera Page UI/UX item 1: lets the UI distinguish capture time
  // (`timestamp`) from upload time when an edge device's upload was
  // delayed (e.g. it was offline and backlogged). Optional for schema
  // resilience; the server always sends it.
  created_at: Iso.optional(),
})
export type Event = z.infer<typeof Event>

export const SiteEventsResponse = z.object({
  events: z.array(Event),
  next_offset: z.number().int().nullable(),
})
export type SiteEventsResponse = z.infer<typeof SiteEventsResponse>

export const EventDetailResponse = z.object({
  event: Event,
  prev_id: z.number().int().nullable(),
  next_id: z.number().int().nullable(),
})
export type EventDetailResponse = z.infer<typeof EventDetailResponse>

// ── /api/sites/{id}/analyses/{type}/{id} ────────────────────────

export const BatchAnalysisDetail = z.object({
  type: z.literal('batch'),
  id: z.number().int(),
  period_start: Iso,
  period_end: Iso,
  summary: z.string(),
  activity: z.string(),
  personnel_count: z.number().int().nullable(),
  weather: z.string(),
  safety: z.record(z.unknown()),
  relevance: z.string(),
  alerts: z.array(z.unknown()),
  events: z.array(z.unknown()),
  event_ids: z.array(z.number().int()),
  created_at: Iso,
})
export type BatchAnalysisDetail = z.infer<typeof BatchAnalysisDetail>

export const SummaryFrame = z.object({
  camera_id: z.string(),
  event_id: z.number().int(),
  timestamp: Iso,
})
export type SummaryFrame = z.infer<typeof SummaryFrame>

export const SummaryAnalysisDetail = z.object({
  type: z.literal('summary'),
  id: z.number().int(),
  level: z.enum(['hourly', 'daily', 'weekly']),
  period_start: Iso,
  period_end: Iso,
  summary: z.string(),
  highlights: z.array(z.string()),
  created_at: Iso,
  frames: z.array(SummaryFrame).optional(),
})
export type SummaryAnalysisDetail = z.infer<typeof SummaryAnalysisDetail>

export const AnalysisDetail = z.discriminatedUnion('type', [
  BatchAnalysisDetail,
  SummaryAnalysisDetail,
])
export type AnalysisDetail = z.infer<typeof AnalysisDetail>

// ── /api/sites/{id}/cameras/{id} ────────────────────────────────

export const CameraSnapshot = z.object({
  event_id: z.number().int(),
  timestamp: Iso,
})
export type CameraSnapshot = z.infer<typeof CameraSnapshot>

export const CameraDetailResponse = z.object({
  site: Site,
  camera: z.object({
    camera_id: z.string(),
    zone: z.string(),
    status: z.string(),
    notes: z.string().optional(),
    display_name: z.string(),
    // Camera Page UI/UX item 7: whether automatic AI analysis is active
    // right now, per this camera's own analysis-hours schedule -- kept
    // deliberately separate from `status` (camera health/reachability).
    // A viewer sees this boolean but never the raw schedule that
    // produced it (that lives behind camera.edit in /analysis-hours).
    analysis_schedule_active: z.boolean(),
  }),
  last_snapshot: CameraSnapshot.nullable(),
})
export type CameraDetailResponse = z.infer<typeof CameraDetailResponse>

export const CameraEventsResponse = z.object({
  events: z.array(Event),
})
export type CameraEventsResponse = z.infer<typeof CameraEventsResponse>

// ── /api/sites/{id}/cameras/{id}/analysis-hours (Camera Page UI/UX item 6) ──

export const AnalysisHours = z.object({
  analysis_start_time: z.string(),
  analysis_end_time: z.string(),
  timezone: z.string(),
  /** 5 Oct 2026: photo checks per hour (1 or 2). Optional for older servers. */
  checks_per_hour: z.number().int().optional(),
  /** 14 Oct 2026: room name shown on the camera card and in the Site update. */
  zone: z.string().optional(),
})
export type AnalysisHours = z.infer<typeof AnalysisHours>

// ── /api/sites/{id}/cameras/{id}/activity (Camera Page UI/UX item 4) ──

export const CameraActivityPhoto = z.object({
  event_id: z.number().int(),
  timestamp: Iso,
  has_image: z.boolean(),
})
export type CameraActivityPhoto = z.infer<typeof CameraActivityPhoto>

/** A batch entry's richer narrative fields -- only present when the
 *  requester holds camera.edit on this camera (server/camera_activity.py).
 *  Mirrors AnalysisDrawer's existing BatchExtras, kept administrative. */
export const CameraActivityBatchEntry = z.object({
  kind: z.literal('batch'),
  id: z.number().int(),
  period_start: Iso,
  period_end: Iso,
  summary: z.string(),
  relevance: z.string().nullable(),
  created_at: Iso,
  photos: z.array(CameraActivityPhoto),
  activity: z.string().optional(),
  personnel_count: z.number().int().nullable().optional(),
  weather: z.string().optional(),
  safety: z.record(z.unknown()).optional(),
  alerts: z.array(z.unknown()).optional(),
})
export type CameraActivityBatchEntry = z.infer<typeof CameraActivityBatchEntry>

/** A raw capture not (yet) covered by any batch narrative -- pending,
 *  skipped, error, or (defensively) an analyzed event outside any batch.
 *  `internal_note` (the raw skip/error text) is admin-only, same gate as
 *  the batch entry's narrative fields above. */
export const CameraActivityCaptureEntry = z.object({
  kind: z.literal('capture'),
  event_id: z.number().int(),
  timestamp: Iso,
  has_image: z.boolean(),
  description: z.string(),
  relevance: z.string(),
  analysis_state: z.string(),
  analysis_state_label: z.string(),
  internal_note: z.string().optional(),
})
export type CameraActivityCaptureEntry = z.infer<typeof CameraActivityCaptureEntry>

export const CameraActivityEntry = z.discriminatedUnion('kind', [
  CameraActivityBatchEntry,
  CameraActivityCaptureEntry,
])
export type CameraActivityEntry = z.infer<typeof CameraActivityEntry>

export const CameraActivityResponse = z.object({
  date: z.string(),
  site_timezone: z.string(),
  entries: z.array(CameraActivityEntry),
})
export type CameraActivityResponse = z.infer<typeof CameraActivityResponse>

// ── /api/sites/{id}/cameras/{id}/baseline (BL-005) ──────────────

export const CameraBaseline = z.object({
  id: z.number().int(),
  description: z.string(),
  reference_image_path: z.string(),
  generated_reason: z.enum([
    'initial',
    'phase_change',
    'weekly_refresh',
    'manual',
    'analysis_area_changed',
    'manual_edit',
  ]),
  phase_at_generation: z.string().nullable(),
  generated_at: Iso,
  superseded_at: Iso.nullable(),
})
export type CameraBaseline = z.infer<typeof CameraBaseline>

// ── /api/sites/{id}/cameras/{id}/analysis-area (WP2A4) ──────────

export const AnalysisArea = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  updated_by_user_id: z.string().nullable(),
  updated_at: Iso.nullable(),
})
export type AnalysisArea = z.infer<typeof AnalysisArea>

export const AnalysisAreaResponse = z.object({
  analysis_area: AnalysisArea.nullable(),
})
export type AnalysisAreaResponse = z.infer<typeof AnalysisAreaResponse>

// ── /api/sites/{id}/cameras/{id}/display-crop/{context} ─────────

/** "grid" = the compact dashboard thumbnail, "detail" = the full camera
 *  page -- the two views a Display Crop can be customized for, each
 *  independently of the other (see server/camera_display_crop.py). */
export const DISPLAY_CROP_CONTEXTS = ['grid', 'detail'] as const
export type DisplayCropContext = (typeof DISPLAY_CROP_CONTEXTS)[number]

export const DisplayCrop = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  updated_by_user_id: z.string().nullable(),
  updated_at: Iso.nullable(),
})
export type DisplayCrop = z.infer<typeof DisplayCrop>

export const DisplayCropResponse = z.object({
  display_crop: DisplayCrop.nullable(),
})
export type DisplayCropResponse = z.infer<typeof DisplayCropResponse>

// ── /api/llm-stats ──────────────────────────────────────────────

export const LlmWindowTotals = z.object({
  calls: z.number().int(),
  cost_usd: z.number(),
  tokens_in: z.number().int(),
  tokens_out: z.number().int(),
  cache_creation_tokens: z.number().int(),
  cache_read_tokens: z.number().int(),
  errors: z.number().int(),
  error_rate: z.number(),
})
export type LlmWindowTotals = z.infer<typeof LlmWindowTotals>

export const LlmByPurpose = z.object({
  purpose: z.string(),
  calls: z.number().int(),
  cost_usd: z.number(),
  tokens_in: z.number().int(),
  tokens_out: z.number().int(),
  cache_creation_tokens: z.number().int(),
  cache_read_tokens: z.number().int(),
  avg_duration_ms: z.number(),
  errors: z.number().int(),
  avg_cost_usd: z.number(),
})
export type LlmByPurpose = z.infer<typeof LlmByPurpose>

export const LlmBySite = z.object({
  site_id: z.string(),
  calls: z.number().int(),
  cost_usd: z.number(),
  tokens_in: z.number().int(),
  tokens_out: z.number().int(),
  errors: z.number().int(),
})
export type LlmBySite = z.infer<typeof LlmBySite>

export const LlmRecentCall = z.object({
  id: z.number().int(),
  created_at: Iso,
  purpose: z.string(),
  site_id: z.string().nullable(),
  model: z.string().nullable(),
  tokens_in: z.number().int(),
  tokens_out: z.number().int(),
  cost_usd: z.number(),
  duration_ms: z.number().int(),
  num_turns: z.number().int(),
  ok: z.boolean(),
  error_message: z.string().nullable(),
})
export type LlmRecentCall = z.infer<typeof LlmRecentCall>

// ── /api/chat, /api/chat/history ────────────────────────────────

export const ChatRole = z.enum(['user', 'assistant'])
export type ChatRole = z.infer<typeof ChatRole>

// Contractor-update workflow fix: mirrors web/src/lib/viewIntent.ts's
// ConfirmCardBlock shape (kept as its own small copy here rather than a
// cross-import, same reasoning as elsewhere in this file) -- what
// server/chatbot.py's /api/chat/history route restores from
// chat_messages.confirm_card_json so a reopened conversation's last
// turn can still show its Save/Edit/Discard or Accept/Keep-as-reported
// card instead of it silently vanishing.
export const ChatConfirmCard = z.object({
  id: z.string(),
  text: z.string(),
  actions: z.array(z.object({ value: z.string(), label: z.string() })),
  payload: z.record(z.unknown()).optional(),
})
export type ChatConfirmCard = z.infer<typeof ChatConfirmCard>

export const ChatMessage = z.object({
  role: ChatRole,
  content: z.string(),
  created_at: Iso.optional(),
  // Contractor-update workflow fix: an array -- a composed reply can
  // carry more than one confirm_card when the admin reported several
  // distinct updates in one message (see ChatSidebar.tsx's Bubble).
  confirm_card: z.array(ChatConfirmCard).optional(),
})
export type ChatMessage = z.infer<typeof ChatMessage>

export const ChatHistoryResponse = z.object({
  conversation_id: z.string(),
  messages: z.array(ChatMessage),
  // Zonoo: Assistant Conversation Behavior -- true only when the last
  // stored row is a user message with nothing after it (an SSE stream
  // cancelled by client disconnect before its final assistant insert
  // ever ran leaves exactly this shape). Combine with "nothing currently
  // in flight for this conversation" client-side before treating it as a
  // genuine interruption -- see useChatConversation's isInterrupted.
  //
  // All four fields below are optional/defaulted rather than strictly
  // required: the real server always sends them (server/chatbot.py's
  // chat_history route), but a handful of existing tests mock this
  // endpoint's response by hand without them, and this keeps those
  // mocks (and any other caller not yet aware of this addition) parsing
  // successfully instead of throwing a zod validation error.
  awaiting_reply: z.boolean().optional(),
  // "running": _run_chat_sync's worker thread is genuinely still
  // executing server-side right now (see server/chatbot.py's
  // _INFLIGHT_REQUESTS). "interrupted": awaiting_reply is true and
  // nothing is running -- offer Retry. "done": last row is an
  // assistant reply (or there are no messages yet).
  reply_status: z.enum(['running', 'interrupted', 'done']).optional(),
  site_id: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  updated_at: Iso.nullable().optional(),
})
export type ChatHistoryResponse = z.infer<typeof ChatHistoryResponse>

// ── /api/chat/conversations (History list) ──────────────────────

export const ChatConversationSummary = z.object({
  conversation_id: z.string(),
  title: z.string().nullable(),
  site_id: z.string().nullable(),
  updated_at: Iso.nullable(),
  created_at: Iso.optional(),
})
export type ChatConversationSummary = z.infer<typeof ChatConversationSummary>

export const ChatConversationsResponse = z.object({
  conversations: z.array(ChatConversationSummary),
})
export type ChatConversationsResponse = z.infer<typeof ChatConversationsResponse>

export const ChatReplyResponse = z.object({
  conversation_id: z.string(),
  reply: z.string().optional(),
  error: z.string().optional(),
})
export type ChatReplyResponse = z.infer<typeof ChatReplyResponse>

export const LlmStats = z.object({
  range: z.object({ from: z.string(), to: z.string() }),
  totals: LlmWindowTotals,
  windows: z.object({
    today: LlmWindowTotals,
    yesterday: LlmWindowTotals,
    last_7d: LlmWindowTotals,
    last_30d: LlmWindowTotals,
  }),
  by_purpose: z.array(LlmByPurpose),
  by_site: z.array(LlmBySite),
  recent: z.array(LlmRecentCall),
})
export type LlmStats = z.infer<typeof LlmStats>

/** 14 Oct 2026: Max's phase-start suggestions (admin only). */
export const MaxSuggestion = z.object({
  phase_id: z.number(),
  phase_name: z.string(),
  suggested_date: z.string(),
  days_seen: z.array(z.string()),
  work_seen: z.array(z.string()),
  photo_event_ids: z.array(z.number()),
  contractor_update: z.string().nullable(),
  planned_start: z.string().nullable().optional(),
  /** 15 Oct 2026 (step 2): "start" or "finish". */
  kind: z.enum(['start', 'finish']).optional(),
  planned_end: z.string().nullable().optional(),
  quiet_days: z.array(z.string()).optional(),
  evidence: z
    .array(z.object({ day: z.string(), work: z.string(), event_id: z.number(), time: z.string() }))
    .optional(),
})
export type MaxSuggestion = z.infer<typeof MaxSuggestion>
export const MaxSuggestionsResponse = z.object({ suggestions: z.array(MaxSuggestion) })

/** 15 Oct 2026: Max's "Seen on camera this week" for the phase in progress. */
export const PhaseSeenEvidence = z.object({ day: z.string(), work: z.string(), event_id: z.number(), time: z.string() })
export type PhaseSeenEvidence = z.infer<typeof PhaseSeenEvidence>
export const PhaseSeenResponse = z.object({ evidence: z.array(PhaseSeenEvidence) })

/** 15 Oct 2026: Alex's site watch. */
const WatchPhoto = z.object({ event_id: z.number(), time: z.string() }).nullable()
export const WatchDay = z.object({
  day: z.string(),
  status: z.enum(['worked', 'nobody', 'today', 'no_photos']),
  /** 15 Oct 2026: real first/last person (movement photos, read once a day). */
  first_person: WatchPhoto.optional(),
  last_person: WatchPhoto.optional(),
  max_workers: z.number(),
  first_seen: z.string().nullable(),
  last_seen: z.string().nullable(),
  start: WatchPhoto,
  end: WatchPhoto,
  photos: z.number(),
})
export type WatchDay = z.infer<typeof WatchDay>
export const SiteWatchResponse = z.object({
  days: z.array(WatchDay),
  week: z
    .object({
      which: z.enum(['this', 'last']),
      from_day: z.string(),
      to_day: z.string(),
      start: z.object({ event_id: z.number(), time: z.string() }),
      end: z.object({ event_id: z.number(), time: z.string() }),
      days_worked: z.number(),
    })
    .nullable(),
})
export type SiteWatchResponse = z.infer<typeof SiteWatchResponse>

/** 16 Oct 2026: Lia step 1, the site floor plan (admin only). */
export const SitePlanResponse = z.object({
  plan: z
    .object({
      filename: z.string().nullable(),
      uploaded_at: z.string().nullable(),
      reading: z.string().nullable(),
      read_at: z.string().nullable(),
      confirmed: z.string().nullable(),
      confirmed_at: z.string().nullable(),
      confirmed_by: z.string().nullable(),
      layout: z.lazy(() => PlanLayout).nullable().optional(),
      confirmed_layout: z.lazy(() => PlanLayout).nullable().optional(),
      has_file: z.boolean(),
      read_running: z.boolean().optional(),
      read_error: z.string().nullable().optional(),
    })
    .nullable(),
})
export type SitePlanResponse = z.infer<typeof SitePlanResponse>

/** 17 Oct 2026: Lia's drawn plan (fractions of the building outline). */
const PlanRect = z.object({ name: z.string(), x: z.number(), y: z.number(), w: z.number(), h: z.number() })
export const PlanLayout = z.object({
  aspect: z.number().optional().default(1.6),
  rooms: z.array(PlanRect),
  outside: z.array(PlanRect).optional().default([]),
  openings: z.array(PlanRect.extend({ kind: z.string().optional() })).optional().default([]),
  walls: z
    .array(z.object({ x0: z.number(), y0: z.number(), x1: z.number(), y1: z.number(), thin: z.boolean().optional() }))
    .optional()
    .default([]),
  elements: z.array(z.object({ name: z.string(), room: z.string().optional().default(''), x: z.number(), y: z.number() })),
  cameras: z.array(
    z.object({
      name: z.string(),
      x: z.number(),
      y: z.number(),
      looks: z.string().optional().default(''),
      sees: z.array(z.string()),
      // 18 Oct 2026: optional view edges in degrees (0 = right, 90 = down).
      fan: z.tuple([z.number(), z.number()]).optional(),
    }),
  ),
})
export type PlanLayout = z.infer<typeof PlanLayout>
export type PlanLayoutInput = z.input<typeof PlanLayout>
export const LiaSpot = z.object({
  name: z.string(),
  room: z.string(),
  x: z.number(),
  y: z.number(),
  level: z.enum(['most', 'some', 'brief']),
  work: z.string().nullable(),
  event_ids: z.array(z.number()),
})
export type LiaSpot = z.infer<typeof LiaSpot>
export const LiaMapResponse = z.object({
  layout: PlanLayout.nullable(),
  spots: z.array(LiaSpot),
  quiet: z.array(z.string()),
  range: z.string(),
  from: z.string().optional(),
  to: z.string().optional(),
  days_with_work: z.number().optional(),
})
export type LiaMapResponse = z.infer<typeof LiaMapResponse>
